import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogActions, DialogTitle, LinearProgress } from "@material-ui/core";
import FSButton from "../Buttons/FSButton";
import { apiInspections } from "../../api/inspections";

const requiresInterior = (type) => type === "A" || type === "B++";

const substituteTemplate = (template, inspection, userFirstName, userFullName) => {
  if (!template) {return "";}
  let msg = template;
  const shortAddr = `${inspection.Address}, ${inspection.City}`;
  const fullAddr = `${inspection.Address}, ${inspection.City}, ${inspection.County}, ${inspection.State}, ${inspection.Zip}`;
  msg = msg.replace(/%shortAddress%/g, shortAddr);
  msg = msg.replace(/%fullAddress%/g, fullAddr);
  msg = msg.replace(/%zipcode%/g, inspection.Zip || "");
  msg = msg.replace(/%agent%/g, inspection.AgentCompany || "");
  msg = msg.replace(/%userFirstName%/g, userFirstName || "");
  msg = msg.replace(/%userFullName%/g, userFullName || "");
  return msg;
};

// view: "main" | "confirm" | "results"
export default function BatchContactDialog({ open, onClose, inspections }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [view, setView] = useState("main");
  const [checkedPolicies, setCheckedPolicies] = useState({});
  const [contactInfo, setContactInfo] = useState({});
  const [interiorTemplate, setInteriorTemplate] = useState("");
  const [exteriorTemplate, setExteriorTemplate] = useState("");
  const [userFirstName, setUserFirstName] = useState("");
  const [userFullName, setUserFullName] = useState("");
  const [sendResults, setSendResults] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!open || !inspections?.length) {return;}

    // Initialize all inspections as checked
    const initial = {};
    inspections.forEach(i => {
      initial[i.PolicyNo] = !!i.PhoneNumber;
    });
    setCheckedPolicies(initial);
    setView("main");
    setSendResults(null);
    setLoadError(null);

    // Fetch contact info
    setLoading(true);
    apiInspections.getContactInfo(inspections.map(i => i.PolicyNo))
      .then(res => {
        if (res.data.status === 200) {
          const result = res.data.result;
          const infoMap = {};
          (result.contacts || []).forEach(c => {
            infoMap[c.policyNo] = c;
          });
          setContactInfo(infoMap);
          setInteriorTemplate(result.interiorTemplate || "");
          setExteriorTemplate(result.exteriorTemplate || "");
          setUserFirstName(result.userFirstName || "");
          setUserFullName(result.userFullName || "");
        }
      })
      .catch(err => {
        console.error("Error loading contact info:", err);
        setLoadError("Failed to load contact information");
      })
      .finally(() => setLoading(false));
  }, [open, inspections]);

  const checkedInspections = useMemo(() => {
    if (!inspections) {return [];}
    return inspections.filter(i => checkedPolicies[i.PolicyNo] && i.PhoneNumber);
  }, [inspections, checkedPolicies]);

  const hasInterior = useMemo(() =>
    checkedInspections.some(i => requiresInterior(i.Type)),
  [checkedInspections]);

  const hasExterior = useMemo(() =>
    checkedInspections.some(i => !requiresInterior(i.Type)),
  [checkedInspections]);

  const exampleExterior = useMemo(() =>
    checkedInspections.find(i => !requiresInterior(i.Type)),
  [checkedInspections]);

  const exampleInterior = useMemo(() =>
    checkedInspections.find(i => requiresInterior(i.Type)),
  [checkedInspections]);

  const toggleCheck = (policyNo) => {
    setCheckedPolicies(prev => ({ ...prev, [policyNo]: !prev[policyNo] }));
  };

  const handleConfirmSend = async () => {
    setSending(true);
    try {
      const policyNos = checkedInspections.map(i => i.PolicyNo);
      const res = await apiInspections.sendBatchText(policyNos, interiorTemplate, exteriorTemplate);
      if (res.data.status === 200) {
        setSendResults(res.data.result.results || []);
      } else {
        setSendResults([{ error: res.data.message || "Failed to send texts" }]);
      }
    } catch (err) {
      console.error("Error sending batch text:", err);
      setSendResults([{ error: "Failed to send texts - please try again" }]);
    } finally {
      setSending(false);
      setView("results");
    }
  };

  const truncate = (str, len) => {
    if (!str) {return "";}
    return str.length > len ? str.substring(0, len) + "..." : str;
  };

  if (!open) {return null;}

  const successCount = sendResults ? sendResults.filter(r => r.success).length : 0;
  const failedResults = sendResults ? sendResults.filter(r => !r.success && r.policyNo) : [];

  return (
    <Dialog open={open} onClose={view === "results" ? onClose : undefined} maxWidth="md" fullWidth>
      {view === "main" &&
        <>
          <DialogTitle>Text Selected Inspections</DialogTitle>
          <DialogContent>
            {loading && <LinearProgress className="tw-mb-4" />}
            {loadError && <p className="tw-text-red-600 tw-mb-4">{loadError}</p>}

            {/* Inspection list */}
            <div className="tw-max-h-96 tw-overflow-y-auto tw-border tw-border-gray-200 tw-rounded tw-mb-4">
              {inspections?.map(inspection => {
                const info = contactInfo[inspection.PolicyNo];
                const hasPhone = !!inspection.PhoneNumber;
                const isInterior = requiresInterior(inspection.Type);
                return (
                  <div key={inspection.PolicyNo}
                    className={`tw-flex tw-items-start tw-gap-3 tw-p-2 tw-border-b tw-border-gray-100 ${!hasPhone ? "tw-opacity-50" : ""}`}>
                    <input
                      type="checkbox"
                      className="tw-mt-1 tw-w-4 tw-h-4"
                      checked={!!checkedPolicies[inspection.PolicyNo]}
                      disabled={!hasPhone}
                      onChange={() => toggleCheck(inspection.PolicyNo)}
                    />
                    <div className="tw-flex-grow tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                        <span className="tw-font-mono tw-font-bold">{inspection.PolicyNo}</span>
                        <span className={`tw-text-xs tw-text-white tw-px-1 tw-rounded ${isInterior ? "tw-bg-purple-500" : "tw-bg-green-600"}`}>
                          {inspection.Type}
                        </span>
                        <span>{inspection.Insured}</span>
                        <span className="tw-text-gray-500">{inspection.Address}, {inspection.City}</span>
                        <span className="tw-text-gray-500">{hasPhone ? inspection.PhoneNumber : "No phone number"}</span>
                      </div>
                      {info?.lastTextTime ?
                        <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5">
                          Last text: {info.lastTextTime} - &quot;{truncate(info.lastTextBody, 60)}&quot;
                        </div>
                        :
                        <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5">No texts sent</div>
                      }
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Variable reference */}
            <div className="tw-text-xs tw-text-gray-500 tw-mb-3">
              Variables: %shortAddress% %fullAddress% %zipcode% %agent% %userFirstName% %userFullName%
            </div>

            {/* Template editors - only show applicable ones */}
            {hasExterior &&
              <div className="tw-mb-4">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                  Exterior template
                </label>
                <textarea
                  className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-p-2 tw-text-sm"
                  rows={8}
                  value={exteriorTemplate}
                  onChange={e => setExteriorTemplate(e.target.value)}
                />
              </div>
            }

            {hasInterior &&
              <div className="tw-mb-4">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                  Interior template
                </label>
                <textarea
                  className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-p-2 tw-text-sm"
                  rows={8}
                  value={interiorTemplate}
                  onChange={e => setInteriorTemplate(e.target.value)}
                />
              </div>
            }
          </DialogContent>
          <DialogActions>
            <FSButton onClick={onClose}>Cancel</FSButton>
            <FSButton
              color="primary"
              disabled={checkedInspections.length === 0 || loading}
              onClick={() => setView("confirm")}
            >
              Send to {checkedInspections.length} inspection{checkedInspections.length !== 1 ? "s" : ""}
            </FSButton>
          </DialogActions>
        </>
      }

      {view === "confirm" &&
        <>
          <DialogTitle>Confirm Send</DialogTitle>
          <DialogContent>
            <p className="tw-mb-4">
              Sending to <strong>{checkedInspections.length}</strong> inspection{checkedInspections.length !== 1 ? "s" : ""} - please check the examples below to ensure the templates are correct.
            </p>

            {hasExterior && exampleExterior &&
              <div className="tw-mb-4">
                <p className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                  Exterior example ({exampleExterior.PolicyNo} - {exampleExterior.Insured}):
                </p>
                <div className="tw-p-3 tw-bg-gray-100 tw-rounded tw-text-sm tw-whitespace-pre-wrap">
                  {substituteTemplate(exteriorTemplate, exampleExterior, userFirstName, userFullName)}
                </div>
              </div>
            }

            {hasInterior && exampleInterior &&
              <div className="tw-mb-4">
                <p className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                  Interior example ({exampleInterior.PolicyNo} - {exampleInterior.Insured}):
                </p>
                <div className="tw-p-3 tw-bg-gray-100 tw-rounded tw-text-sm tw-whitespace-pre-wrap">
                  {substituteTemplate(interiorTemplate, exampleInterior, userFirstName, userFullName)}
                </div>
              </div>
            }
          </DialogContent>
          <DialogActions>
            <FSButton onClick={() => setView("main")} disabled={sending}>Back</FSButton>
            <FSButton
              color="primary"
              disabled={sending}
              submitting={sending}
              onClick={handleConfirmSend}
            >
              Confirm &amp; Send
            </FSButton>
          </DialogActions>
        </>
      }

      {view === "results" &&
        <>
          <DialogTitle>
            {failedResults.length === 0 ? "Texts Sent" : "Send Results"}
          </DialogTitle>
          <DialogContent>
            {failedResults.length === 0 ?
              <div className="tw-text-center tw-py-4">
                <i className="fa fa-check-circle tw-text-green-500 tw-text-4xl tw-mb-3" />
                <p className="tw-text-lg">
                  Successfully sent to {successCount} inspection{successCount !== 1 ? "s" : ""}
                </p>
              </div>
              :
              <div>
                {successCount > 0 &&
                  <p className="tw-text-green-600 tw-mb-2">
                    <i className="fa fa-check-circle tw-mr-1" />
                    {successCount} sent successfully
                  </p>
                }
                <p className="tw-text-red-600 tw-mb-2">
                  <i className="fa fa-times-circle tw-mr-1" />
                  {failedResults.length} failed:
                </p>
                <div className="tw-border tw-border-red-200 tw-rounded tw-p-2">
                  {failedResults.map(r => 
                    <div key={r.policyNo} className="tw-text-sm tw-py-1">
                      <span className="tw-font-mono tw-font-bold">{r.policyNo}</span>
                      <span className="tw-text-red-500 tw-ml-2">{r.error}</span>
                    </div>
                  )}
                </div>
              </div>
            }
          </DialogContent>
          <DialogActions>
            <FSButton color="primary" onClick={onClose}>Done</FSButton>
          </DialogActions>
        </>
      }
    </Dialog>
  );
}
